/**
 * Client-side Word (.docx) generator using the `docx` v9 library.
 * Conservative, widely-supported feature set: headings, paragraphs,
 * runs, tables with per-cell shading. Disclaimers use a 1-row table
 * (instead of Paragraph-level borders which are rendered inconsistently
 * across Word/LibreOffice).
 */
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  Table,
  TableRow,
  TableCell,
  WidthType,
} from "docx";
import { saveAs } from "file-saver";

const GOLD = "C9A84C";
const BLUE = "1A3C5E";
const RED = "C0392B";

function p(text, opts = {}) {
  return new Paragraph({
    alignment: opts.alignment || AlignmentType.LEFT,
    spacing: { after: 120 },
    children: [
      new TextRun({
        text: String(text ?? ""),
        bold: !!opts.bold,
        italics: !!opts.italics,
        color: opts.color,
        size: opts.size,
      }),
    ],
  });
}

function h1(text, color = BLUE) {
  return new Paragraph({
    heading: HeadingLevel.HEADING_1,
    spacing: { before: 360, after: 180 },
    children: [new TextRun({ text, bold: true, color })],
  });
}

function h2(text) {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    spacing: { before: 220, after: 120 },
    children: [new TextRun({ text, bold: true, color: BLUE })],
  });
}

function cell(text, { bold = false, shaded = false, color, align } = {}) {
  return new TableCell({
    shading: shaded ? { fill: "F3EBD0" } : { fill: "FFFFFF" },
    children: [
      new Paragraph({
        alignment: align || AlignmentType.LEFT,
        children: [new TextRun({ text: String(text ?? ""), bold, color })],
      }),
    ],
  });
}

function table(rows) {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: rows.map((r) => new TableRow({ children: r })),
  });
}

function fmt(n, digits = 2) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("fr-FR", {
    maximumFractionDigits: digits,
  });
}

function pct(n) {
  if (n == null) return "—";
  return `${fmt(n, 2)}%`;
}

function disclaimerTable() {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({
        children: [
          new TableCell({
            shading: { fill: "FDECEA" },
            children: [
              new Paragraph({
                alignment: AlignmentType.LEFT,
                children: [
                  new TextRun({
                    text:
                      "⚠ DOCUMENT PÉDAGOGIQUE — Ce rapport est un outil " +
                      "d'aide à la réflexion conforme à la Nouvelle Théorie " +
                      "des Finances Publiques (ELY Mustapha). Il n'a AUCUNE " +
                      "valeur juridictionnelle.",
                    bold: true,
                    color: RED,
                  }),
                ],
              }),
            ],
          }),
        ],
      }),
    ],
  });
}

export async function generateWord({ project, analysis, filename }) {
  // eslint-disable-next-line no-console
  console.log("[report] generateWord start", { filename });

  const r = analysis?.results || {};
  const currency = r.summary?.currency || "UM";
  const legal = r.legal_analysis;
  const judgment = r.judgment;
  const profiles = r.profiles || [];
  const budgetLines = r.aggregated_budget_lines || [];
  const debt = r.debt || {};
  const national = r.national || {};
  const today = new Date().toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const children = [];

  // Page 1 — Title
  children.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 2400, after: 200 },
      children: [
        new TextRun({ text: "VITAE-PUBLICA™", bold: true, size: 64, color: BLUE }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({
          text: "Rapport d'Analyse Vitaliste des Finances Publiques",
          bold: true,
          size: 30,
          color: BLUE,
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 400 },
      children: [
        new TextRun({
          text: `${project?.country || "—"}  |  Exercice ${
            project?.fiscal_year || "—"
          }  |  ${today}`,
          italics: true,
          size: 22,
        }),
      ],
    }),
    p(""),
    p("Auteur de la méthodologie : Ahmed ELY Mustapha", {
      alignment: AlignmentType.CENTER,
      bold: true,
      color: GOLD,
    }),
    p("Programmeur : ELY Mustapha — PMP · I-PMP · IBM Full Stack Developer", {
      alignment: AlignmentType.CENTER,
      italics: true,
    }),
    p(""),
    disclaimerTable()
  );

  // Page 2 — Synthèse
  children.push(
    new Paragraph({
      pageBreakBefore: true,
      heading: HeadingLevel.TITLE,
      children: [
        new TextRun({ text: "Synthèse exécutive", bold: true, color: BLUE }),
      ],
    })
  );
  const ind0 = profiles[0]?.individual || {};
  children.push(
    table([
      [
        cell("Indicateur vitaliste", { bold: true, shaded: true }),
        cell("Valeur", { bold: true, shaded: true }),
      ],
      [cell("Jours de vie sacrifiés / an"), cell(fmt(ind0.jours_vie_sacrifies_par_an))],
      [cell("Mois de vie sacrifiés"), cell(fmt(ind0.mois_vie_sacrifies))],
      [cell("Années carrière sacrifiées"), cell(fmt(ind0.annees_vie_sur_carriere))],
      [cell("% vie fiscale"), cell(pct(ind0.ratio_vie_fiscale_pct))],
      [cell("Années vie hypothéquées (dette)"), cell(fmt(debt.annees_vie_hypothequees))],
    ])
  );

  // Pages 3-4 — Profils
  children.push(h1("Profil du contribuable modèle"));
  profiles.forEach((prof) => {
    const i = prof.individual || {};
    children.push(h2(prof.profile_name || "Profil"));
    children.push(
      table([
        [
          cell("Paramètre", { bold: true, shaded: true }),
          cell("Valeur", { bold: true, shaded: true }),
        ],
        [cell("Salaire mensuel"), cell(`${fmt(i.salaire_mensuel)} ${currency}`)],
        [cell("Salaire journalier (= Mensuel/30)"), cell(`${fmt(i.salaire_journalier)} ${currency}`)],
        [cell("Total prélèvements / an"), cell(`${fmt(i.total_prelevements)} ${currency}`)],
        [cell("Jours de vie sacrifiés / an (= Total / Journalier)"), cell(fmt(i.jours_vie_sacrifies_par_an))],
        [cell("Années carrière sacrifiées"), cell(fmt(i.annees_vie_sur_carriere))],
      ])
    );
  });

  // Pages 5-7 — Budget
  children.push(h1("Budget national converti en vies humaines"));
  if (national?.available) {
    children.push(
      p(
        `Années cumulées d'existence consacrées au budget : ${fmt(national.annees_vies_budget)} années.`
      )
    );
  }
  children.push(h2("Postes budgétaires agrégés"));
  children.push(
    table([
      [
        cell("Secteur", { bold: true, shaded: true }),
        cell("Montant", { bold: true, shaded: true }),
        cell("%", { bold: true, shaded: true }),
      ],
      ...budgetLines.slice(0, 20).map((bl) => [
        cell(bl.sector || "—"),
        cell(`${fmt(bl.amount)} ${currency}`),
        cell(pct(bl.percentage)),
      ]),
    ])
  );

  // Pages 8-9 — Dette
  children.push(h1("Endettement public — conversion vitaliste"));
  if (debt?.available) {
    children.push(
      table([
        [cell("Dette par contribuable (UM)"), cell(`${fmt(debt.dette_par_contribuable)} ${currency}`)],
        [cell("Années de vie hypothéquées"), cell(fmt(debt.annees_vie_hypothequees))],
      ])
    );
  } else {
    children.push(p("Aucune donnée de dette publique disponible pour cet exercice."));
  }

  // Pages 10-12 — Juridique
  if (legal) {
    children.push(h1("Analyse juridique"));
    if (legal.synthese) children.push(p(legal.synthese, { italics: true }));
    if (legal.infractions?.length) {
      children.push(h2("Infractions potentielles identifiées"));
      children.push(
        table([
          [
            cell("Qualification", { bold: true, shaded: true }),
            cell("Montant (UM)", { bold: true, shaded: true }),
            cell("Jours de vie", { bold: true, shaded: true }),
            cell("Gravité", { bold: true, shaded: true }),
          ],
          ...legal.infractions.map((inf) => [
            cell(inf.qualification || ""),
            cell(fmt(inf.montant_um)),
            cell(fmt(inf.jours_de_vie)),
            cell(`Niveau ${inf.niveau_gravite}`, {
              color:
                inf.niveau_gravite === 3
                  ? RED
                  : inf.niveau_gravite === 2
                  ? "B87A1C"
                  : "1F7A3E",
              bold: true,
            }),
          ]),
        ])
      );
    }
    ["professionnelle", "civile", "administrative", "penale"].forEach((k) => {
      const items = legal.responsabilites?.[k] || [];
      if (items.length) {
        children.push(h2(`Responsabilité ${k}`));
        items.forEach((it) => {
          children.push(p(`• ${it.description}`));
          if (it.articles?.length) {
            children.push(
              p(`   Articles : ${it.articles.join(", ")}`, { italics: true })
            );
          }
        });
      }
    });
  }

  // Pages 13-15 — Jugement
  if (judgment) {
    children.push(h1("Jugement vitaliste simulé"));
    children.push(
      p(judgment.tribunal || "Tribunal Fictif Vitaliste", {
        bold: true,
        alignment: AlignmentType.CENTER,
      })
    );
    children.push(
      p(judgment.header || "AU NOM DU PEUPLE ET DE SA VIE", {
        bold: true,
        alignment: AlignmentType.CENTER,
      })
    );
    children.push(h2("Exposé des faits (volet financier)"));
    children.push(p(judgment.expose_des_faits?.financier || "—"));
    children.push(h2("Exposé des faits (volet vitaliste)"));
    children.push(p(judgment.expose_des_faits?.vitaliste || "—", { bold: true }));
    children.push(h2("Motifs vitalistes"));
    children.push(p(judgment.motifs_vitalistes || "—", { italics: true }));
    children.push(h2("Dispositif"));
    children.push(
      table([
        [cell("Branche", { bold: true, shaded: true }), cell("Décision", { bold: true, shaded: true })],
        [cell("Sanctions pénales"), cell(judgment.dispositif?.sanctions_penales || "—")],
        [
          cell("Réparation civile"),
          cell(`${fmt(judgment.dispositif?.reparation_civile_en_UM)} ${currency}`),
        ],
        [
          cell("Réparation vitaliste"),
          cell(judgment.dispositif?.reparation_vitaliste || "—", { bold: true }),
        ],
        [cell("Sanctions administratives"), cell(judgment.dispositif?.sanctions_administratives || "—")],
      ])
    );
    if (judgment.citation_finale) {
      children.push(
        p(`« ${judgment.citation_finale} »`, {
          italics: true,
          alignment: AlignmentType.CENTER,
        })
      );
    }
    children.push(disclaimerTable());
  }

  // Page finale — Méthodologie
  children.push(h1("Méthodologie et références"));
  children.push(
    p("Formule vitaliste canonique : Jours_de_vie = Prélèvements / (Salaire_mensuel / 30).", {
      bold: true,
    })
  );
  children.push(
    p(
      "Unité de compte des finances publiques : la vie humaine. " +
        "Unité de mesure : le jour de vie, dérivé du salaire mensuel de référence du contribuable."
    )
  );
  children.push(
    p(
      "Référence : ELY Mustapha, « La Nouvelle Théorie des Finances Publiques — Les Finances Publiques sont des Tranches de Vies ».",
      { italics: true }
    )
  );
  children.push(disclaimerTable());

  const doc = new Document({
    creator: "VITAE-PUBLICA",
    title: filename,
    description: "Rapport d'Analyse Vitaliste des Finances Publiques (pédagogique).",
    sections: [{ children }],
  });

  const blob = await Packer.toBlob(doc);
  // eslint-disable-next-line no-console
  console.log("[report] generateWord ready, size=", blob.size);
  saveAs(blob, filename);
  return blob.size;
}
